import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { emptySankeyFormState } from 'utils/form';

import { MAX_UNDOS, STATE_HISTORY_DEBOUNCE_MS } from '../../constants';
import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { SankeyStateHistoryContext } from '../../contexts/sankeyStateHistoryContext';
import { hasPressedRedo, hasPressedUndo } from '../../utils/keyboard';
import { useIsDataFromExcel } from '../DataEditor/utils';
import { compareHistoryStates } from './utils';

type Props = {
  children: React.ReactNode;
};

/*
 * This hook is used to keep track of the state history of the Sankey form data
 * and to provide undo and redo functionality to the user.
 */
const SankeyStateHistoryProvider: React.FC<Props> = ({ children }) => {
  const isDataFromExcel = useIsDataFromExcel();

  const { form } = useSankeyFormContext();
  const formState = JSON.stringify(form.watch());

  // Keep encoded JSON strings of the form data in the history array
  // for easier object deep comparison and to avoid unnecessary re-renders.
  const [history, setHistory] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;
  const isInitialized = history.length > 0;

  const resetForm = useCallback((historyState: string) => {
    form.reset(JSON.parse(historyState), { keepDirty: true });
  }, []);

  const undo = useCallback(() => {
    if (canUndo) {
      setCurrentIndex(currentIndex - 1);
      resetForm(history[currentIndex - 1]);
    }
  }, [canUndo, currentIndex, history]);

  const redo = useCallback(() => {
    if (canRedo) {
      setCurrentIndex(currentIndex + 1);
      resetForm(history[currentIndex + 1]);
    }
  }, [canRedo, currentIndex, history]);

  const debouncedUpdateHistory = useMemo(
    () =>
      debounce((formState: string) => {
        // Do nothing if the form data is empty as this means the form is not initialized
        if (formState === emptySankeyFormState) {
          return;
        }

        // Compare old and new flows state to determine if the change happened
        const { hasChangedFlows, hasChangedIDs } = compareHistoryStates(
          formState,
          history[currentIndex]
        );

        // Initialize the history if not yet initialized or if Sankey ID changed or if the last change
        // came from Excel flows data update (in which case this serves to reset the history).
        if (
          !isInitialized ||
          hasChangedIDs ||
          (isDataFromExcel && hasChangedFlows)
        ) {
          setHistory([formState]);
          setCurrentIndex(0);
          return;
        }

        // Finally, if the form data is not equal to the current state, add the new state to the history
        // and update the current index. If the history has exceeded the maximum number of undos, remove
        // the oldest state from the history.
        if (formState !== history[currentIndex]) {
          const hasExceededMaxUndos = history.length >= MAX_UNDOS;
          const newHistory = history.slice(
            hasExceededMaxUndos ? 1 : 0,
            currentIndex + 1
          );
          setHistory([...newHistory, formState]);
          setCurrentIndex(newHistory.length);
        }
      }, STATE_HISTORY_DEBOUNCE_MS),
    [history, currentIndex, isInitialized, isDataFromExcel]
  );

  // This effect is responsible for updating the history when the form data changes
  useEffect(() => debouncedUpdateHistory(formState), [formState]);

  // This effects sets up the global handler for undo and redo keyboard shortcuts.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (hasPressedUndo(event)) {
        undo();
      } else if (hasPressedRedo(event)) {
        redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [undo, redo]);

  return (
    <SankeyStateHistoryContext.Provider
      value={{
        canUndo,
        canRedo,
        undo,
        redo,
      }}
    >
      {children}
    </SankeyStateHistoryContext.Provider>
  );
};

export default SankeyStateHistoryProvider;
